/**
 * Where Human Resources is, from the URL hash, so every view is a link that
 * can be shared (WALLIYO-0131):
 *
 * - `#hr` and `#hr/directory` — look anybody up;
 * - `#hr/directory?q=diallo` — that search;
 * - `#hr/directory/81478068` — one person, by the number on their badge;
 * - `#hr/recruiting`;
 * - `#hr/employees` — the records, the first sub-tab;
 * - `#hr/employees/time-off` — and the other six;
 * - `#hr/employees/leaving?who=<staffMemberId>` — winding one person down;
 * - `#hr/learning`.
 *
 * Four tabs rather than ten, because the gate is a tab: Recruiting and
 * Employees need MANAGE_HUMAN_RESOURCES, and closing Employees closes the
 * seven sub-tabs under it in one go. Directory and Learning need nothing.
 */

import type { StaffFeatureId } from "@wallyo/contracts/actions";
import { HR_SCREEN_FEATURES } from "@wallyo/contracts/console/hr";

export const HR_TABS = ["directory", "recruiting", "employees", "learning"] as const;
export type HrTab = (typeof HR_TABS)[number];

/** Everything under Employees. Records is where it opens. */
export const HR_SUB_TABS = ["records", "joining", "time-off", "performance", "payroll", "reviews", "leaving"] as const;
export type HrSubTab = (typeof HR_SUB_TABS)[number];

/** The tabs behind the feature. The other two are open to anybody signed in. */
const GATED: ReadonlySet<HrTab> = new Set<HrTab>(["recruiting", "employees"]);

export function tabNeedsFeature(tab: HrTab): boolean {
  return GATED.has(tab);
}

/** True when this person may open the HR half. The server checks again. */
export function canOpenHr(features: readonly StaffFeatureId[]): boolean {
  return HR_SCREEN_FEATURES.some((feature) => features.includes(feature));
}

export type HrRoute = {
  tab: HrTab;
  /** Only meaningful on `employees`; Records elsewhere, so the type stays flat. */
  subTab: HrSubTab;
  /** The directory search, or "" for everybody. */
  query: string;
  /** Whose page is open, from `#hr/directory/<uuid>`. Null on the list. */
  uuid: number | null;
  /**
   * Which employee a sub-tab is about, from `?who=`. A sub-tab that is about
   * one person is still that sub-tab, so this is a parameter rather than a
   * deeper path: `#hr/employees/leaving` and `…?who=…` are the same place,
   * with and without somebody chosen.
   */
  who: string | null;
};

export const EMPTY_HR_ROUTE: HrRoute = {
  tab: "directory",
  subTab: "records",
  query: "",
  uuid: null,
  who: null,
};

/**
 * A hash nobody can open lands on the directory rather than on a locked tab:
 * an old link to Payroll, followed by somebody who is not in HR, should show
 * them the thing they can see instead of a wall.
 */
export function parseHrHash(hash: string, features: readonly StaffFeatureId[]): HrRoute {
  const [path = "", search = ""] = hash.replace(/^#\/?hr\/?/, "").split("?", 2);
  const [first = "", second = ""] = path.split("/");
  const params = new URLSearchParams(search);

  let tab: HrTab = HR_TABS.find((each) => each === first) ?? "directory";
  if (tabNeedsFeature(tab) && !canOpenHr(features)) tab = "directory";

  let subTab: HrSubTab = "records";
  let uuid: number | null = null;
  if (tab === "employees") {
    subTab = HR_SUB_TABS.find((each) => each === second) ?? "records";
  } else if (tab === "directory" && /^\d+$/.test(second)) {
    // A UUID is a number. A name in that slot names nobody, so it opens the
    // list rather than a page that cannot load.
    uuid = Number(second);
  }

  const who = params.get("who")?.trim() ?? "";
  return { tab, subTab, query: params.get("q")?.trim() ?? "", uuid, who: who === "" ? null : who };
}

export function hrHref(route: Partial<HrRoute> = {}): string {
  const { tab = "directory", subTab = "records", query = "", uuid = null, who = null } = route;
  let path = `#hr/${tab}`;
  if (tab === "employees" && subTab !== "records") path += `/${subTab}`;
  if (tab === "directory" && uuid !== null) path += `/${uuid}`;
  // The search belongs to the list, not to somebody's page: keeping it on both
  // would make "back to the results" and "this person" the same link.
  if (tab === "directory" && uuid === null && query !== "") {
    path += `?${new URLSearchParams({ q: query }).toString()}`;
  }
  if (tab === "employees" && who !== null) {
    path += `?${new URLSearchParams({ who }).toString()}`;
  }
  return path;
}

/**
 * The directory's own route: `#directory`, `#directory?q=diallo`, `#directory/81478068`.
 *
 * The same two questions the HR screen's directory tab answered — a search, or
 * one person by the number on their badge — now that it is a screen of its own
 * (WALLIYO-0134).
 */
/**
 * Where the directory's own links point (WALLIYO-0137).
 *
 * The same two views — the list and one person — are mounted twice: as the
 * Human Resources screen's Directory tab, and as the Directory screen every
 * workspace lists. They must link within whichever one is showing. Building
 * every link with `hrHref` sent anybody who searched, or opened a name, from
 * `#directory` into `#hr/directory` — which in the Executive workspace is
 * labelled Workforce, so the whole screen appeared to change out from under
 * them.
 */
export type DirectoryLinks = {
  /** The list, with a search or without one. */
  list: (query?: string) => string;
  /** One person, by the number on their badge. */
  person: (uuid: number) => string;
};

/** Mounted inside the Human Resources screen, as its Directory tab. */
export const HR_DIRECTORY_LINKS: DirectoryLinks = {
  list: (query = "") => hrHref({ tab: "directory", query }),
  person: (uuid) => hrHref({ tab: "directory", uuid }),
};

/** Mounted as the Directory screen, which every workspace lists. */
export const DIRECTORY_LINKS: DirectoryLinks = {
  list: (query = "") => (query === "" ? "#directory" : `#directory?${new URLSearchParams({ q: query }).toString()}`),
  person: (uuid) => `#directory/${uuid}`,
};

export function parseDirectoryHash(
  hash: string,
  _features: readonly StaffFeatureId[],
): { query: string; uuid: number | null } {
  const [path = "", search = ""] = hash.replace(/^#\/?directory\/?/, "").split("?", 2);
  const params = new URLSearchParams(search);
  return {
    query: params.get("q")?.trim() ?? "",
    uuid: /^\d+$/.test(path) ? Number(path) : null,
  };
}
